import { createRouter, createWebHistory, RouteRecordRaw } from 'vue-router'
import { getToken } from '@/utils/auth'

const routes: RouteRecordRaw[] = [
  {
    path: '/',
    redirect: '/dashboard',
  },
  {
    path: '/auth',
    component: () => import('@/layouts/AuthLayout.vue'),
    children: [
      {
        path: 'login',
        name: 'Login',
        component: () => import('@/views/auth/Login.vue'),
        meta: { title: '登录' },
      },
      {
        path: 'agreement',
        name: 'UserAgreement',
        component: () => import('@/views/auth/Agreement.vue'),
        meta: { title: '用户协议' },
      },
      {
        path: 'privacy',
        name: 'PrivacyPolicy',
        component: () => import('@/views/auth/Privacy.vue'),
        meta: { title: '隐私政策' },
      },
    ],
  },
  {
    path: '/',
    component: () => import('@/layouts/MainLayout.vue'),
    meta: { requiresAuth: true },
    children: [
      {
        path: 'dashboard',
        name: 'Dashboard',
        component: () => import('@/views/dashboard/index.vue'),
        meta: { title: '工作台' },
      },
      {
        path: 'contract',
        children: [
          {
            path: 'list',
            name: 'ContractList',
            component: () => import('@/views/contract/list.vue'), // ✅ 改回原生Vue组件
            meta: { title: '合同管理' },
          },
          {
            path: 'detail/:id',
            name: 'ContractDetail',
            component: () => import('@/views/contract/detail.vue'), // ✅ 新增详情页面
            meta: { title: '合同详情' },
          },
          {
            path: 'sign/:id',
            name: 'ContractSign',
            component: () => import('@/views/contract/SignIframe.vue'), // ✅ 签署页面保持iframe
            meta: { title: '签署合同' },
          },
          {
            path: 'create',
            name: 'ContractCreate',
            component: () => import('@/views/contract/CreateSelectIframe.vue'), // ✅ 创建方式选择页面
            meta: { title: '创建合同' },
          },
          {
            path: 'create-template',
            name: 'ContractCreateTemplate',
            component: () => import('@/views/contract/CreateIframe.vue'), // ✅ 模板创建页面
            meta: { title: '使用模板创建', sealCorePath: '/pc/sign/create' },
          },
          {
            path: 'create-file',
            name: 'ContractCreateFile',
            component: () => import('@/views/contract/CreateIframe.vue'), // ✅ 文件上传创建页面
            meta: { title: '上传文件创建', sealCorePath: '/pc/sign/create-file' },
          },
          {
            path: 'create-image',
            name: 'ContractCreateImage',
            component: () => import('@/views/contract/CreateIframe.vue'), // ✅ 图片上传创建页面
            meta: { title: '上传图片创建', sealCorePath: '/pc/sign/create-image' },
          },
          {
            path: 'form/:id',
            name: 'ContractForm',
            component: () => import('@/views/contract/FormIframe.vue'), // ✅ 表单填写页面
            meta: { title: '填写表单' },
          },
          {
            path: 'position-designer',
            name: 'ContractPositionDesigner',
            component: () => import('@/views/contract/PositionDesignerIframe.vue'), // ✅ 签名位置设计器
            meta: { title: '设计签名位置' },
          },
        ],
      },
      {
        path: 'seal',
        children: [
          {
            path: 'list',
            name: 'SealList',
            component: () => import('@/views/seal/list.vue'),
            meta: { title: '印章管理' },
          },
          {
            path: 'create',
            name: 'SealCreate',
            component: () => import('@/views/seal/CreateIframe.vue'),
            meta: { title: '创建印章' },
          },
          {
            path: 'template',
            name: 'TemplateList',
            component: () => import('@/views/seal/template/index.vue'),
            meta: { title: '合同模版' },
          },
          {
            path: 'template/create',
            name: 'TemplateCreate',
            component: () => import('@/views/seal/TemplateCreateIframe.vue'),
            meta: { title: '创建模板' },
          },
          {
            path: 'template-category',
            name: 'TemplateCategory',
            component: () => import('@/views/seal/templateCategory/index.vue'),
            meta: { title: '模版分类' },
          },
        ],
      },
      {
        path: 'batch',
        children: [
          {
            path: 'create/:templateId?',
            name: 'BatchCreate',
            component: () => import('@/views/seal/BatchCreateIframe.vue'),
            meta: { title: '批量发起' },
          },
          {
            path: 'list',
            name: 'BatchList',
            component: () => import('@/views/seal/BatchListIframe.vue'),
            meta: { title: '批量任务' },
          },
          {
            path: 'detail/:batchId',
            name: 'BatchDetail',
            component: () => import('@/views/seal/BatchDetailIframe.vue'),
            meta: { title: '批量任务详情' },
          },
        ],
      },
      {
        path: 'enterprise',
        children: [
          {
            path: 'create',
            name: 'EnterpriseCreate',
            component: () => import('@/views/enterprise/create.vue'),
            meta: { title: '创建企业' },
          },
          {
            path: 'member',
            name: 'EnterpriseMember',
            component: () => import('@/views/enterprise/member.vue'),
            meta: { title: '企业成员' },
          },
          {
            path: 'verify',
            name: 'EnterpriseVerify',
            component: () => import('@/views/enterprise/verify.vue'),
            meta: { title: '企业认证' },
          },
        ],
      },
      {
        path: 'profile',
        children: [
          {
            path: 'info',
            name: 'ProfileInfo',
            component: () => import('@/views/profile/info.vue'),
            meta: { title: '个人信息' },
          },
          {
            path: 'authentication',
            name: 'ProfileAuthentication',
            component: () => import('@/views/profile/authentication.vue'),
            meta: { title: '实名认证' },
          },
          {
            path: 'quota/purchase',
            name: 'QuotaPurchase',
            component: () => import('@/views/quota/Purchase.vue'),
            meta: { title: '购买套餐' },
          },
          {
            path: 'quota/pay',
            name: 'QuotaPay',
            component: () => import('@/views/quota/Pay.vue'),
            meta: { title: '订单支付' },
          },
        ],
      },
      // 合同验证页面（放在MainLayout内以显示侧边栏）
      {
        path: 'verify',
        name: 'ContractVerify',
        component: () => import('@/views/verify/index.vue'),
        meta: { title: '合同验真', requiresAuth: false },
      },
    ],
  },
  // H5移动端页面（无需登录验证）
]

const router = createRouter({
  history: createWebHistory(),
  routes,
})

// 路由守卫
router.beforeEach((to, _, next) => {
  const token = getToken()
  
  // H5页面无需登录验证
  if (to.path.startsWith('/h5')) {
    next()
    return
  }
  
  // 合同验证页面无需登录验证
  if (to.path === '/verify' || to.name === 'ContractVerify') {
    next()
    return
  }
  
  // 标记为不需要认证的页面
  if (to.meta.requiresAuth === false) {
    next()
    return
  }
  
  // 检查父路由是否需要认证
  const parentRequiresAuth = to.matched.some(record => record.meta.requiresAuth === true)
  
  if (parentRequiresAuth && !token) {
    // 如果当前页面明确标记不需要认证，则放行
    if (to.meta.requiresAuth === false) {
      next()
    } else {
      next('/auth/login')
    }
  } else if (to.path === '/auth/login' && token) {
    next('/dashboard')
  } else {
    next()
  }
})

export const resetRouter = (): void => {
  const resetWhiteNameList = ['Login', 'UserAgreement', 'PrivacyPolicy']
  router.getRoutes().forEach((route) => {
    const { name } = route
    if (name && !resetWhiteNameList.includes(name as string)) {
      router.hasRoute(name) && router.removeRoute(name)
    }
  })
}

export default router
